import { FC, useState } from 'react'
import { Button, Result, Skeleton } from 'antd'
import ListContentLayout from 'layout/ListContentLayout'
import { globalToaster } from 'components/elements/global'
import { useDeleteDomainMutation, useGetDomainStatusQuery } from '../api'
import { TDomainType, TRedirectDomain } from '../types'
import { getErrorMessage, isConnected } from '../utils'
import DomainWizard from './DomainWizard'
import RedirectDomainsSection from './RedirectDomainsSection'
import DnsRecordsModal from './DnsRecordsModal'
import DeleteRedirectModal from './DeleteRedirectModal'

type TWizardState = {
 mode: TDomainType
 // Set for "View setup instructions": opens on step 3 for this domain
 resumeDomain?: string
}

// Settings → Domain. One of three states, from GET /domain-status:
// no primary (empty) / pending_ns (waiting) / ns_verified|legacy (connected)
const DomainSettings: FC = () => {
 const { data, isLoading, isError, error, refetch } = useGetDomainStatusQuery(
  undefined,
  { refetchOnMountOrArgChange: true },
 )
 const [wizard, setWizard] = useState<TWizardState | null>(null)
 // Domain (primary or a connected redirect) whose records modal is open
 const [recordsFor, setRecordsFor] = useState<{
  id: number
  domain: string
 } | null>(null)
 // Redirect awaiting delete confirmation
 const [deletingRedirect, setDeletingRedirect] =
  useState<TRedirectDomain | null>(null)
 const [deleteDomain, { isLoading: isDeleting }] = useDeleteDomainMutation()

 const primary = data?.primary ?? null
 const redirects = data?.redirects ?? []

 const openWizard = (mode: TDomainType, resumeDomain?: string): void =>
  setWizard({ mode, resumeDomain })

 // Removes the redirect; DOMAIN_STATUS invalidation refreshes the list
 const onDeleteRedirect = async (): Promise<void> => {
  if (!deletingRedirect) return
  try {
   await deleteDomain(deletingRedirect.id).unwrap()
   globalToaster({ type: 'success', content: 'Redirect domain deleted' })
   setDeletingRedirect(null)
  } catch (err) {
   globalToaster({
    type: 'error',
    content: getErrorMessage(err, 'Could not delete this domain. Try again.'),
   })
  }
 }

 const renderState = (): JSX.Element => {
  if (!primary) {
   return (
    <div className="card">
     <div className="card-body empty-state">
      <h2>Connect your domain</h2>
      <p className="sub">
       Connect to send email from CRM and deploy your website under your own
       name.
      </p>
      <Button
       type="primary"
       icon={<i className="fas fa-link" />}
       onClick={() => openWizard('primary')}
      >
       Connect domain
      </Button>
     </div>
    </div>
   )
  }

  if (!isConnected(primary.ns_status)) {
   const failed = primary.ns_status === 'failed'
   return (
    <div className="card">
     <div className="card-head">
      <div className={`icon-circle ${failed ? 'failed' : 'wait'}`}>
       <i className={failed ? 'fas fa-times' : 'fas fa-clock'} />
      </div>
      <h2>{primary.domain}</h2>
      {failed ? (
       <span className="status-pill failed">
        <i className="fas fa-times" /> Connection failed
       </span>
      ) : (
       <span className="status-pill pending">
        <i className="fas fa-clock" /> Waiting for nameserver change
       </span>
      )}
     </div>
     <div className="card-body">
      <p className="sub">
       {failed
        ? 'We couldn’t connect this domain. Check the nameservers at your registrar, or change the domain and try again.'
        : 'We’re checking automatically and will email you the moment this domain is connected — no need to keep checking yourself.'}
      </p>
      <div className="btn-row">
       <Button
        icon={<i className="fas fa-eye" />}
        onClick={() => openWizard('primary', primary.domain)}
       >
        View setup instructions
       </Button>
       <Button
        icon={<i className="fas fa-pen" />}
        onClick={() => openWizard('primary')}
       >
        Change domain
       </Button>
      </div>
     </div>
    </div>
   )
  }

  return (
   <>
    <div className="card">
     <div className="card-head">
      <div className="icon-circle done">
       <i className="fas fa-check" />
      </div>
      <h2>{primary.domain}</h2>
      <span className="status-pill live">
       <i className="fas fa-check" /> Connected
      </span>
      <button
       type="button"
       className="change-link"
       onClick={() => openWizard('primary')}
      >
       Change domain
      </button>
     </div>
     <div className="card-foot">
      <button
       type="button"
       className="link-btn"
       onClick={() => setRecordsFor(primary)}
      >
       <i className="fas fa-tasks" /> Manage DNS Records
      </button>
     </div>
    </div>
    <RedirectDomainsSection
     redirects={redirects}
     primaryDomain={primary.domain}
     onAdd={() => openWizard('redirect')}
     onViewInstructions={(domain) => openWizard('redirect', domain)}
     onManageRecords={(redirect) => setRecordsFor(redirect)}
     onDelete={(redirect) => setDeletingRedirect(redirect)}
    />
   </>
  )
 }

 // Same full-width header + body layout as the other Settings pages
 return (
  <ListContentLayout title="Domain Settings" bodyClass="domain-settings-page">
   {isLoading && <Skeleton active paragraph={{ rows: 3 }} />}

   {isError && (
    <Result
     status="warning"
     title="Could not load your domain settings"
     subTitle={getErrorMessage(error, 'Please try again.')}
     extra={
      <Button type="primary" onClick={refetch}>
       Try again
      </Button>
     }
    />
   )}

   {!isLoading && !isError && renderState()}

   <DomainWizard
    visible={!!wizard}
    mode={wizard?.mode ?? 'primary'}
    primary={primary}
    resumeDomain={wizard?.resumeDomain}
    onClose={() => setWizard(null)}
   />
   <DnsRecordsModal domain={recordsFor} onClose={() => setRecordsFor(null)} />
   <DeleteRedirectModal
    redirect={deletingRedirect}
    deleting={isDeleting}
    onCancel={() => setDeletingRedirect(null)}
    onConfirm={onDeleteRedirect}
   />
  </ListContentLayout>
 )
}

export default DomainSettings
